<template>
  <div ref="viewerContainer" class="photo-sphere-viewer-container"></div>
</template>

<script>
import { log, warn, error } from '@/utils/debugLogger';
import { Viewer } from '@photo-sphere-viewer/core';
import { EquirectangularTilesAdapter } from '@photo-sphere-viewer/equirectangular-tiles-adapter';
import { MarkersPlugin } from '@photo-sphere-viewer/markers-plugin';
import { OverlaysPlugin } from '@photo-sphere-viewer/overlays-plugin';
import '@photo-sphere-viewer/core/index.css';
import '@photo-sphere-viewer/markers-plugin/index.css';

export default {
  name: 'PhotoSphereViewer',
  props: {
    panoramaUrl: {
      type: [String, Object],
      required: true,
    },
    markers: {
      type: Array,
      default: () => [],
    },
    overlays: {
      type: Array,
      default: () => [],
    },
    initialView: {
      type: Object,
      default: () => ({ yaw: 0, pitch: 0, zoom: 50 }),
    },
    useTiles: {
      type: Boolean,
      default: false,
    },
    tilesConfig: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      viewer: null,
      markersPlugin: null,
      overlaysPlugin: null,
      isLoading: true,
    };
  },
  mounted() {
    this.initViewer();
  },
  beforeDestroy() {
    this.destroyViewer();
  },
  methods: {
    initViewer() {
      try {
        this.isLoading = true;
        this.$emit('loading', true);

        // Determine if we need to use tiles adapter
        const isTiled = typeof this.panoramaUrl === 'object' && this.panoramaUrl.baseUrl;

        // Configure viewer options
        const viewerConfig = {
          container: this.$refs.viewerContainer,
          panorama: this.panoramaUrl,
          defaultYaw: this.initialView.yaw,
          defaultPitch: this.initialView.pitch,
          defaultZoomLvl: this.initialView.zoom,
          navbar: false,
          plugins: [
            [MarkersPlugin, {
              markers: this.markers,
            }],
            [OverlaysPlugin, {
              overlays: this.overlays,
            }],
          ],
          loadingImg: null,
          loadingTxt: 'Loading panorama...',
        };

        // Add adapter for tiled panoramas
        if (isTiled) {
          viewerConfig.adapter = EquirectangularTilesAdapter;
        }

        // Initialize Photo Sphere Viewer
        this.viewer = new Viewer(viewerConfig);

        // Get plugin instances
        this.markersPlugin = this.viewer.getPlugin(MarkersPlugin);
        this.overlaysPlugin = this.viewer.getPlugin(OverlaysPlugin);

        // Listen to ready event
        this.viewer.addEventListener('ready', () => {
          this.isLoading = false;
          this.$emit('loading', false);
          this.$emit('ready', this.viewer);
        });

        // Listen to click events
        this.viewer.addEventListener('click', (e) => {
          this.$emit('click', e);
        });

        // Listen to marker events
        if (this.markersPlugin) {
          this.markersPlugin.addEventListener('select-marker', ({ marker }) => {
            this.$emit('marker-select', marker);
          });

          this.markersPlugin.addEventListener('unselect-marker', ({ marker }) => {
            this.$emit('marker-unselect', marker);
          });
        }

      } catch (err) {
        error('Error initializing Photo Sphere Viewer:', err);
        this.$emit('error', err);
        this.isLoading = false;
        this.$emit('loading', false);
      }
    },
    destroyViewer() {
      if (this.viewer) {
        this.viewer.destroy();
        this.viewer = null;
        this.markersPlugin = null;
        this.overlaysPlugin = null;
      }
    },
    // Public methods to control the viewer
    addMarker(marker) {
      if (this.markersPlugin) {
        try {
          // Check if marker already exists
          const existingMarker = this.markersPlugin.getMarker(marker.id);
          if (existingMarker) {
            // Update existing marker instead of adding
            this.markersPlugin.updateMarker({
              id: marker.id,
              ...marker
            });
          } else {
            // Add new marker
            this.markersPlugin.addMarker(marker);
          }
        } catch (error) {
          // If getMarker throws error, marker doesn't exist, so add it
          this.markersPlugin.addMarker(marker);
        }
      }
    },
    removeMarker(markerId) {
      if (this.markersPlugin) {
        this.markersPlugin.removeMarker(markerId);
      }
    },
    updateMarker(marker) {
      if (this.markersPlugin) {
        this.markersPlugin.updateMarker(marker);
      }
    },
    clearMarkers() {
      if (this.markersPlugin) {
        this.markersPlugin.clearMarkers();
      }
    },
    addOverlay(overlay) {
      if (this.overlaysPlugin) {
        this.overlaysPlugin.addOverlay(overlay);
      }
    },
    removeOverlay(overlayId) {
      if (this.overlaysPlugin) {
        this.overlaysPlugin.removeOverlay(overlayId);
      }
    },
    clearOverlays() {
      if (this.overlaysPlugin) {
        this.overlaysPlugin.clearOverlays();
      }
    },
    setPanorama(url) {
      if (this.viewer) {
        this.viewer.setPanorama(url);
      }
    },
    rotate(position) {
      if (this.viewer) {
        this.viewer.rotate(position);
      }
    },
    zoom(level) {
      if (this.viewer) {
        this.viewer.zoom(level);
      }
    },
    getPosition() {
      if (this.viewer) {
        return this.viewer.getPosition();
      }
      return null;
    },
    getZoomLevel() {
      if (this.viewer) {
        return this.viewer.getZoomLevel();
      }
      return 0;
    },
  },
  watch: {
    panoramaUrl(newUrl) {
      if (newUrl && this.viewer) {
        this.setPanorama(newUrl);
      }
    },
    markers: {
      handler(newMarkers) {
        if (this.markersPlugin) {
          // Get current marker IDs
          const currentMarkerIds = this.markersPlugin.getMarkers().map(m => m.id);
          const newMarkerIds = newMarkers.map(m => m.id);

          // Remove markers that are no longer in the new list
          currentMarkerIds.forEach(id => {
            if (!newMarkerIds.includes(id)) {
              try {
                this.markersPlugin.removeMarker(id);
              } catch (error) {
                // Marker already removed, ignore
              }
            }
          });

          // Add or update markers
          newMarkers.forEach(marker => this.addMarker(marker));
        }
      },
      deep: true,
    },
    overlays: {
      handler(newOverlays) {
        if (this.overlaysPlugin) {
          this.clearOverlays();
          newOverlays.forEach(overlay => this.addOverlay(overlay));
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.photo-sphere-viewer-container {
  width: 100%;
  height: 100%;
  position: relative;
}

/* Override default Photo Sphere Viewer styles if needed */
::v-deep .psv-container {
  border-radius: 8px;
  cursor: grab !important;
}

::v-deep .psv-loader-container {
  background: rgba(0, 0, 0, 0.5);
}
</style>
